import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MedicalCheck from '../assets/icons/Medicalcheck.svg';
import useMedicalQuestionnaireProgress from '../hooks/useMedicalQuestionnaireProgress';
import useRefreshQuestionnaireProgressFromProfile from '../hooks/useRefreshQuestionnaireProgressFromProfile';
import {
  fetchMedicalQuestionnaireResumeState,
  loadMedicalQuestionnaireSummaryContext,
  resolveNextQuestionOneBased,
} from '../services/medicalQuestionnaire';
import { setMedicalQuestionnaireTotalSections } from '../store/medicalQuestionnaireProgressSlice';

function WhiteCheckIcon({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M5.166 13.775a1.1 1.1 0 01-1.21 0L.295 9.614a1.1 1.1 0 010-1.556 1.1 1.1 0 011.556 0L5.166 11.632 14.278 2.52a1.1 1.1 0 011.557 1.556 1.1 1.1 0 01-1.557 1.557L6.377 13.273a1.1 1.1 0 01-1.21.502z"
        fill="currentColor"
      />
    </svg>
  );
}

function QuestionnaireSummaryPageLoader() {
  return (
    <div
      className="flex min-h-[calc(100vh-5rem)] flex-col items-center justify-center gap-4 px-6"
      role="status"
      aria-live="polite"
      aria-label="Pagina laden"
    >
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#C1DCD4] border-t-[#119975]" />
      <p className="font-poppins text-[15px] text-[#777777]">Antwoorden laden…</p>
    </div>
  );
}

function ChevronDown({ open }) {
  return (
    <svg
      className={`h-4 w-4 shrink-0 text-[#BBBBBB] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SectionsCollapseToggle({ collapsed, onToggle, disabled }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Alle secties uitklappen' : 'Alle secties inklappen'}
      className="flex h-6 w-[2.375rem] items-center justify-center rounded-md border border-[#D1D5DB] bg-[#F5F6F8] text-[#2D4343] transition-colors hover:border-[#B8BEC6] hover:bg-[#ECEEF1] hover:text-[#1A1A1A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#119975] disabled:cursor-not-allowed disabled:opacity-40"
    >
      <svg
        className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

export default function QuestionnaireSummaryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const summarySelf = searchParams.get('self') === 'true';
  const { totalSections, filledSections } = useMedicalQuestionnaireProgress();
  const [openIndices, setOpenIndices] = useState(() => new Set());
  const [allSectionsExpandedByToggle, setAllSectionsExpandedByToggle] = useState(false);
  const [sections, setSections] = useState([]);
  const [summaryCompletedSectionsPercentage, setSummaryCompletedSectionsPercentage] =
    useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNavigatingToEdit, setIsNavigatingToEdit] = useState(false);
  const [loadError, setLoadError] = useState('');

  const displayPercent =
    summaryCompletedSectionsPercentage != null
      ? Math.min(100, Math.max(0, Math.round(summaryCompletedSectionsPercentage)))
      : 0;
  const completedFromSections = sections.filter((section) => section.complete).length;
  const totalFromSections = sections.length;
  const displayCompleted =
    totalFromSections > 0 ? completedFromSections : filledSections;
  const displayTotal = totalFromSections > 0 ? totalFromSections : totalSections;
  const showSectionCounts = displayTotal > 0;

  useRefreshQuestionnaireProgressFromProfile(true);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setIsLoading(true);
      setLoadError('');
      setSummaryCompletedSectionsPercentage(null);
      try {
        const { sections: summarySections, stats } =
          await loadMedicalQuestionnaireSummaryContext({
            signal: controller.signal,
            self: summarySelf,
          });

        if (!summarySections?.length) {
          throw new Error('No questionnaire sections');
        }

        if (stats?.totalSections > 0) {
          dispatch(setMedicalQuestionnaireTotalSections(stats.totalSections));
        }

        setSummaryCompletedSectionsPercentage(stats?.completedSectionsPercentage ?? 0);
        setSections(summarySections);
      } catch (error) {
        const cancelled =
          controller.signal.aborted ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED';
        if (cancelled) return;
        setLoadError("We couldn't load your answers. Please refresh the page and try again.");
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };
    load();
    return () => controller.abort();
  }, [dispatch, summarySelf]);

  const toggleSection = (index) => {
    setOpenIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAllSections = () => {
    if (sections.length === 0) return;
    setAllSectionsExpandedByToggle((prev) => {
      const next = !prev;
      setOpenIndices(() =>
        next ? new Set(sections.map((_, index) => index)) : new Set()
      );
      return next;
    });
  };

  const handleEditAll = useCallback(async () => {
    if (isNavigatingToEdit) return;
    setIsNavigatingToEdit(true);
    try {
      const { prefillAnswers, numberOfQuestionsFilled } =
        await fetchMedicalQuestionnaireResumeState();
      const nextQuestion = resolveNextQuestionOneBased(numberOfQuestionsFilled, 0);
      navigate('/medical-questionnaire/questions', {
        state: {
          prefillAnswers,
          nextQuestion,
        },
      });
    } catch {
      navigate('/medical-questionnaire/questions');
    } finally {
      setIsNavigatingToEdit(false);
    }
  }, [isNavigatingToEdit, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9F5F2] font-poppins">
        <QuestionnaireSummaryPageLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F5F2] font-poppins py-8 md:py-12">
      <div className="mx-auto max-w-[1040px] px-6 pb-16">
        <div className="mb-10 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#E1F5EE]">
            <img src={MedicalCheck} alt="" aria-hidden="true" className="h-6 w-6" />
          </div>
          <h1 className="font-libreBaskerville text-[32px] font-normal tracking-[-0.5px] text-[#1A1A1A]">
            Jouw antwoorden
          </h1>
          <p className="mt-2 text-[15px] text-[#777777]">
            Bekijk je antwoorden. Gebruik de knop hieronder om verder te gaan met de vragenlijst.
          </p>
        </div>

        <div className="mb-5">
          <div
            className="h-[5px] w-full overflow-hidden rounded-full bg-[#E0DDD6]"
            role="progressbar"
            aria-valuenow={Math.round(displayPercent)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Voortgang vragenlijst"
          >
            <div
              className="h-full rounded-full bg-[#119975] transition-[width] duration-500 ease-out"
              style={{ width: `${Math.min(100, Math.max(0, displayPercent))}%` }}
            />
          </div>
          <div
            className={`mt-2 flex items-start gap-4 font-poppins text-[14px] ${
              showSectionCounts ? 'justify-between' : 'justify-end'
            }`}
          >
            {showSectionCounts && (
              <p className="text-[#777777]">
                <span className="font-semibold tabular-nums text-[#1A1A1A]">
                  {displayCompleted}/{displayTotal}
                </span>
                {' secties'}
              </p>
            )}
            <p className="shrink-0 text-right font-semibold tabular-nums text-biowell-green">
              {Math.round(displayPercent)}%
            </p>
          </div>
        </div>

        {loadError && (
          <p className="mb-4 text-center text-[15px] text-[#B42318]" role="alert">
            {loadError}
          </p>
        )}

        {sections.length > 0 && (
          <div className="space-y-4">
            {sections.map((section, index) => {
              const isOpen = openIndices.has(index);
              const isFirstSection = index === 0;
              return (
                <div
                  key={section.title}
                  className={isFirstSection ? 'relative pt-7' : undefined}
                >
                  {isFirstSection && (
                    <div className="absolute right-0 top-0">
                      <SectionsCollapseToggle
                        collapsed={!allSectionsExpandedByToggle}
                        onToggle={toggleAllSections}
                        disabled={sections.length === 0}
                      />
                    </div>
                  )}
                  <div className="overflow-hidden rounded-[14px] border border-[#E0DDD6] bg-white">
                    <button
                      type="button"
                      onClick={() => toggleSection(index)}
                      className="flex w-full items-center justify-between px-[1.4rem] py-[1.1rem] text-left transition-colors hover:bg-[#FAF9F7]"
                    >
                      <div className="flex min-w-0 items-center gap-3.5">
                        <div
                          className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-xs font-medium ${
                            section.complete
                              ? 'bg-[#119975] text-white'
                              : 'bg-[#E1F5EE] text-[#0F6E56]'
                          }`}
                        >
                          {section.complete ? (
                            <WhiteCheckIcon className="h-3.5 w-3.5 text-white" />
                          ) : (
                            index + 1
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[15px] font-medium text-[#1A1A1A]">
                            {section.title}
                          </div>
                          <div className="mt-0.5 text-xs text-[#999999]">{section.subtitle}</div>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2.5">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                            section.complete
                              ? 'bg-[#DDF0EA] text-[#119975]'
                              : 'bg-[#FAEEDA] text-[#854F0B]'
                          }`}
                        >
                          {section.complete ? 'Volledig' : 'Incompleet'}
                        </span>
                        <ChevronDown open={isOpen} />
                      </div>
                    </button>

                    {isOpen && (
                      <div className="border-t border-[#F0EDE6] px-[1.4rem] pb-5 pt-4">
                        {section.qa.map((row) => (
                          <div
                            key={row.q}
                            className="flex items-baseline justify-between border-b border-[#F0EDE6] py-1.5 last:border-b-0"
                          >
                            <span className="flex-1 pr-4 text-[13px] text-[#888888]">{row.q}</span>
                            <span className="text-right text-[13px] font-medium text-[#1A1A1A]">
                              {row.a}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {summarySelf && (
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={handleEditAll}
              disabled={Boolean(loadError) || isNavigatingToEdit}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#0f8a6b] px-8 py-[15px] font-poppins text-[15px] font-medium text-white transition-colors hover:bg-[#0d7a5f] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isNavigatingToEdit ? 'Laden…' : 'Alles bewerken'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
